import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Wallet, Smartphone, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { api, formatApiError } from "../lib/api";

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/invite/${token}`); setInvite(data); setName(data.name); }
      catch (e) { setError(formatApiError(e.response?.data?.detail, "Invite not found.")); }
    })();
  }, [token]);

  const accept = async () => {
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    setBusy(true);
    try {
      const { data } = await api.post(`/invite/${token}/accept`, { name, password });
      localStorage.setItem("orello_token", data.token);
      toast.success("Welcome to Orello!");
      // Navigate to dashboard
      // The popstate listener in AuthContext will prevent going back
      nav("/app");
      window.location.reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="px-6 py-5 max-w-7xl mx-auto w-full"><Link to="/"><Logo size="sm" /></Link></header>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md card-base p-8" data-testid="invite-page">
          {error ? <div className="text-red-600 text-sm">{error}</div> : !invite ? <div className="text-gray-500 text-sm">Loading…</div> : (
            <>
              <h1 className="text-xl font-bold font-display">{invite.agency_name || invite.broker_admin_name} has invited you to join Orello</h1>
              <p className="text-sm text-gray-500 mt-1">Create deals and earn ₹150 on every completed deposit.</p>

              <div className="mt-5 grid grid-cols-3 gap-2 text-xs text-gray-700">
                <div className="card-base p-3 text-center"><Wallet className="h-4 w-4 mx-auto text-emerald-600" /><div className="mt-1">₹150 per deal</div></div>
                <div className="card-base p-3 text-center"><Smartphone className="h-4 w-4 mx-auto text-[#F97316]" /><div className="mt-1">Phone-first</div></div>
                <div className="card-base p-3 text-center"><BadgeCheck className="h-4 w-4 mx-auto text-amber-600" /><div className="mt-1">Your wallet</div></div>
              </div>

              <div className="mt-6 space-y-4">
                <div>
                  <label className="text-sm font-medium">Your name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm" data-testid="invite-name" />
                </div>
                <div>
                  <label className="text-sm font-medium">Mobile</label>
                  <div className="mt-1 px-3 py-2.5 bg-gray-50 rounded-lg text-sm text-gray-700">{invite.phone}</div>
                </div>
                <div>
                  <label className="text-sm font-medium">Create a password</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 6 characters" className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm" data-testid="invite-password" />
                </div>
                <button onClick={accept} disabled={busy} className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white rounded-lg font-medium disabled:opacity-50" data-testid="invite-accept">
                  {busy ? "Joining…" : "Accept Invite & Sign Up"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
